import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import HeatMeter from "@/components/base/HeatMeter";
import TagBadge from "@/components/base/TagBadge";
import { hotTopics, topicCategories } from "@/mocks/topics";

type SortKey = "heat" | "shares" | "spread";

const sortTabs: { key: SortKey; label: string; hint: string }[] = [
  { key: "heat", label: "热度榜", hint: "按综合热度排序" },
  { key: "shares", label: "转发榜", hint: "按累计转发量排序" },
  { key: "spread", label: "出圈榜", hint: "按扩散速度与增幅排序" },
];

const formatNumber = (value: number) =>
  value >= 10000 ? `${(value / 10000).toFixed(1)}万` : String(value);

export default function RankingList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortKey, setSortKey] = useState<SortKey>("heat");
  const [keyword, setKeyword] = useState(searchParams.get("q") || "");

  const activeCategory = searchParams.get("c") || "all";

  const setCategory = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === "all") {
      next.delete("c");
    } else {
      next.set("c", id);
    }
    setSearchParams(next, { replace: true });
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (keyword.trim()) {
      next.set("q", keyword.trim());
    } else {
      next.delete("q");
    }
    setSearchParams(next, { replace: true });
  };

  const list = useMemo(() => {
    const q = keyword.trim();
    const filtered = hotTopics.filter((topic) => {
      const matchCategory = activeCategory === "all" || topic.categoryId === activeCategory;
      const matchKeyword = !q || topic.title.includes(q) || topic.summary.includes(q);
      return matchCategory && matchKeyword;
    });

    const sorted = [...filtered].sort((a, b) => {
      if (sortKey === "shares") return b.shares - a.shares;
      if (sortKey === "spread") return b.trendValue - a.trendValue;
      return b.heat - a.heat;
    });
    return sorted;
  }, [activeCategory, keyword, sortKey]);

  const maxHeat = hotTopics[0].heat;
  const activeTab = sortTabs.find((tab) => tab.key === sortKey);

  return (
    <div>
      <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex rounded-full bg-background-200/70 p-1">
            {sortTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setSortKey(tab.key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  sortKey === tab.key
                    ? "bg-primary-500 font-medium text-background-50"
                    : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSearch} className="flex w-full gap-2 lg:w-80">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索话题关键词"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 border-t border-background-200 pt-4">
          {topicCategories.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => setCategory(category.id)}
              className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                activeCategory === category.id
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-sm text-foreground-600">
        <p>
          共 <strong className="font-heading text-foreground-950">{list.length}</strong> 条话题 ·{" "}
          {activeTab?.hint}
        </p>
        <p className="text-xs text-foreground-400">榜单每 15 分钟刷新一次</p>
      </div>

      {list.length === 0 ? (
        <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
          <i className="ri-emotion-happy-line text-3xl text-foreground-400" />
          <p className="mt-3 font-heading text-base font-bold text-foreground-950">
            没有找到匹配的话题
          </p>
          <p className="mt-2 text-sm text-foreground-600">
            换一个关键词，或者点击“全部风向”看看其他分类。
          </p>
        </div>
      ) : (
        <ol className="mt-4 space-y-3">
          {list.map((topic, index) => (
            <li key={topic.id}>
              <article className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:p-5">
                <div className="flex items-start gap-4">
                  <span
                    className={`w-8 shrink-0 font-heading text-xl font-black ${
                      index < 3 ? "text-primary-600" : "text-foreground-300"
                    }`}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <TagBadge tag={topic.tag} />
                      <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900">
                        {topic.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-foreground-500">
                        <i
                          className={
                            topic.trend === "up"
                              ? "ri-arrow-up-line text-primary-600"
                              : topic.trend === "down"
                                ? "ri-arrow-down-line text-secondary-500"
                                : "ri-subtract-line text-foreground-400"
                          }
                        />
                        {topic.trend === "down" ? "-" : "+"}
                        {topic.trendValue}%
                      </span>
                    </div>

                    <h2 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
                      <Link to={`/topic/${topic.id}`} className="transition-colors hover:text-primary-600">
                        {topic.title}
                      </Link>
                    </h2>
                    <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                      {topic.summary}
                    </p>

                    <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-fire-line text-primary-500" />
                        热度 {formatNumber(topic.heat)}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-share-forward-line text-accent-600" />
                        转发 {formatNumber(topic.shares)}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-git-branch-line" />
                        峰值 {topic.peakTime}
                      </span>
                    </div>
                  </div>

                  <div className="hidden w-40 shrink-0 flex-col justify-between self-stretch md:flex">
                    <HeatMeter value={topic.heat} max={maxHeat} />
                    <Link
                      to={`/topic/${topic.id}`}
                      className="mt-3 inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-500 hover:text-primary-600"
                    >
                      查看轨迹
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </div>

                <Link
                  to={`/topic/${topic.id}`}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary-600 md:hidden"
                >
                  查看扩散轨迹
                  <i className="ri-arrow-right-line" />
                </Link>
              </article>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}